import React from "react";
import RegisterPage from "../components/RegisterPage";
import { useHSE } from "../lib/store";

const PRIO = {
  Low: { bg: "#DCFCE7", color: "#166534" },
  Medium: { bg: "#FEF3C7", color: "#854D0E" },
  High: { bg: "#FFE4E6", color: "#9F1239" },
  Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
};
const STATUS = {
  Open: { bg: "#FEF3C7", color: "#854D0E" },
  "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
  Closed: { bg: "#DCFCE7", color: "#166534" },
  Overdue: { bg: "#FEE2E2", color: "#7F1D1D" },
};

const COL_BG = { Open: "#FEF3C7", "In Progress": "#DBEAFE", Closed: "#DCFCE7", Overdue: "#FEE2E2" };
const COL_BAR = { Open: "#F59E0B", "In Progress": "#2563EB", Closed: "#18B84F", Overdue: "#EF4444" };

export default function ActionPlan() {
  const { data, siteNames } = useHSE();
  const legalRefIds = (data.legalRefs || []).map((l) => l.id);
  const counts = ["Open", "In Progress", "Closed", "Overdue"].map((s) => ({
    status: s, items: data.actions.filter((a) => a.status === s),
  }));

  const KanbanPanel = (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {counts.map((c) => (
        <div key={c.status} className="command-card p-3" style={{ background: COL_BG[c.status] }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: COL_BAR[c.status] }} />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-[#082B4D]">{c.status}</span>
            </div>
            <span className="font-heading font-bold text-lg tabular text-[#082B4D]">{c.items.length}</span>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {c.items.slice(0, 5).map((a) => (
              <div key={a.id} className="bg-white/85 rounded-md px-2 py-1.5 text-[11px] border border-white/60">
                <div className="font-mono text-[10px] text-slate-500">{a.id}{a.obsRefId && ` - ${a.obsRefId}`}</div>
                <div className="text-slate-700 line-clamp-2">{a.description}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{a.owner} - {a.dueDate}</div>
              </div>
            ))}
            {c.items.length === 0 && <div className="text-[11px] text-slate-400 italic px-2">No items</div>}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <RegisterPage
      title="Corrective Actions"
      subtitle="Centralized construction HSE corrective actions with findings, audit and inspection linkage"
      storeKey="actions"
      idPrefix="CA"
      topPanels={KanbanPanel}
      columns={[
        { key: "id", label: "Action ID" },
        { key: "source", label: "Source" },
        { key: "obsRefId", label: "Finding ID", render: (r) => r.obsRefId ? <span className="font-mono text-[11px] text-[#2563EB]">{r.obsRefId}</span> : <span className="text-slate-300">-</span> },
        { key: "site", label: "Site" },
        { key: "department", label: "Area / Department" },
        { key: "description", label: "Corrective Action" },
        { key: "legalRef", label: "Legal Ref", render: (r) => r.legalRef ? <span className="font-mono text-[11px] text-[#082B4D]">{r.legalRef}</span> : <span className="text-slate-300">-</span> },
        { key: "priority", label: "Priority" },
        { key: "owner", label: "Responsible Person" },
        { key: "dueDate", label: "Target Date" },
        { key: "daysOverdue", label: "Days Overdue", render: (r) => r.daysOverdue > 0 ? <span className="text-rose-600 font-semibold tabular">{r.daysOverdue}</span> : <span className="text-slate-400">-</span> },
        { key: "status", label: "Status" },
        { key: "completionDate", label: "Closed Date" },
      ]}
      badgeMap={{ priority: PRIO, status: STATUS }}
      formFields={[
        { key: "id", label: "Action ID" },
        { key: "source", label: "Source", type: "select", options: ["Daily Safety Checklist", "Weekly Site Inspection", "Monthly Internal Audit", "Equipment Inspection", "Finding / Observation"] },
        { key: "obsRefId", label: "Finding / Observation ID" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "department", label: "Audit Area / Department", type: "select", options: data.settings.departments },
        { key: "description", label: "Corrective Action", type: "textarea", full: true },
        { key: "legalRef", label: "Legal Reference", type: "select", options: legalRefIds },
        { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "owner", label: "Responsible Person" },
        { key: "dueDate", label: "Target Date", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Closed", "Overdue"] },
        { key: "completionDate", label: "Closure Date", type: "date" },
        { key: "remarks", label: "Remarks", type: "textarea", full: true },
      ]}
    />
  );
}
